import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileSpreadsheet, Printer, RefreshCw, Loader2, X, TreePine, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import { downloadExcel } from '../../utils/excel';
import { printTable } from '../../utils/printTable';
import { fmt4, keyLabel } from '../../utils/cft';
import { useSettings } from '../../context/SettingsContext';
import { KIND, useWoodNames } from './WoodStockReport';

// CFT page → Stock Dashboard: one card per wood type, one row per thickness category
// (incl. ALL CFT). Opening = stock before From; Closing = Opening + Inward + Return
// + Transfer In − Transfer Out − Outward. Click a row for its dated ledger.
const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const monthStart = () => { const d = new Date(); return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); };
const fmtDate = d => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const cell = v => (Number(v) ? fmt4(v) : '—');
const TAGS = ['bg-blue-50 text-blue-700', 'bg-green-50 text-green-700', 'bg-amber-50 text-amber-800', 'bg-purple-50 text-purple-700'];

export default function WoodStockDashboard() {
  const { settings } = useSettings() || {};
  const woods = useWoodNames();
  const [f, setF] = useState({ from: monthStart(), to: ymd(new Date()), wood: '', bucket: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState(null);        // { wood, bucket }
  const set = p => setF(x => ({ ...x, ...p }));

  const load = useCallback(async () => {
    if (f.from && f.to && f.from > f.to) return toast.error('From date must be on or before To date');
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
      const r = await api.get('/wood-stock/thickness-summary', { params });
      setData(r.data);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  // Thickness filter options: what the shown woods have
  const bucketOpts = useMemo(() => {
    const keys = new Set();
    (data?.woods || []).forEach(w => w.buckets.forEach(b => keys.add(b.key)));
    if (f.bucket) keys.add(f.bucket);
    return [...keys].sort((a, b) => (a === 'ALL') - (b === 'ALL') || Number(a) - Number(b));
  }, [data, f.bucket]);

  const t = data?.totals;
  const anyTransfer = t && (t.transfer_in || t.transfer_out);
  const period = `${f.from ? fmtDate(f.from) : 'Start'} – ${f.to ? fmtDate(f.to) : 'Today'}`;
  const filterText = [period, f.wood && `Wood: ${f.wood}`, f.bucket && `Thickness: ${keyLabel(f.bucket)}`].filter(Boolean).join(' · ');

  const flatRows = () => (data?.woods || []).flatMap(w => [
    ...w.buckets.map(b => ({ wood: w.wood_name, thickness: b.label, ...b })),
    { wood: w.wood_name, thickness: 'TOTAL', ...w.totals, total: true },
  ]);
  const exportExcel = async () => {
    try {
      const rows = flatRows().map(r => ({
        'Wood Type': r.wood, Thickness: r.thickness, 'Opening CFT': r.opening, 'Inward CFT': r.inward, 'Outward (Issue) CFT': r.outward,
        'Return CFT': r.return, 'Transfer In CFT': r.transfer_in, 'Transfer Out CFT': r.transfer_out, 'Closing CFT': r.closing,
      }));
      if (t) rows.push({ 'Wood Type': 'GRAND TOTAL', Thickness: '', 'Opening CFT': t.opening, 'Inward CFT': t.inward, 'Outward (Issue) CFT': t.outward, 'Return CFT': t.return, 'Transfer In CFT': t.transfer_in, 'Transfer Out CFT': t.transfer_out, 'Closing CFT': t.closing });
      await downloadExcel(`Wood_Stock_by_Thickness_${f.to || ymd(new Date())}.xlsx`, [
        { name: 'Summary', rows: [['Report', 'Wood Stock by Thickness'], ['Period', period], ['Wood', f.wood || 'All'], ['Thickness', f.bucket ? keyLabel(f.bucket) : 'All']] },
        { name: 'Stock by Thickness', rows },
      ]);
    } catch (err) { toast.error(err.message); }
  };
  const print = () => {
    printTable({
      title: 'Wood Stock by Thickness', subtitle: filterText, company: settings?.company_name,
      summary: t ? [['Opening', fmt4(t.opening)], ['Inward', fmt4(t.inward)], ['Outward (Issue)', fmt4(t.outward)], ['Return', fmt4(t.return)],
        ...(anyTransfer ? [['Transfer In / Out', `${fmt4(t.transfer_in)} / ${fmt4(t.transfer_out)}`]] : []), ['Closing', fmt4(t.closing)]] : [],
      columns: [
        { label: 'Wood Type', value: r => r.wood }, { label: 'Thickness', value: r => r.thickness },
        { label: 'Opening', align: 'r', value: r => fmt4(r.opening) }, { label: 'Inward', align: 'r', value: r => fmt4(r.inward) },
        { label: 'Outward', align: 'r', value: r => fmt4(r.outward) }, { label: 'Return', align: 'r', value: r => fmt4(r.return) },
        ...(anyTransfer ? [{ label: 'Transfer In', align: 'r', value: r => fmt4(r.transfer_in) }, { label: 'Transfer Out', align: 'r', value: r => fmt4(r.transfer_out) }] : []),
        { label: 'Closing', align: 'r', value: r => fmt4(r.closing) },
      ],
      rows: flatRows(), rowClass: r => (r.total ? 'open' : r.closing < 0 ? 'neg' : ''),
    }) || toast.error('Allow pop-ups to print');
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <label className="text-xs text-gray-500">From<input type="date" value={f.from} onChange={e => set({ from: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-500">To<input type="date" value={f.to} onChange={e => set({ to: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-500">Wood Type
          <select value={f.wood} onChange={e => set({ wood: e.target.value, bucket: '' })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-800">
            <option value="">All wood types</option>{woods.map(n => <option key={n} value={n}>{n}</option>)}
          </select></label>
        <label className="text-xs text-gray-500">Thickness
          <select value={f.bucket} onChange={e => set({ bucket: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-800">
            <option value="">All thicknesses</option>{bucketOpts.map(k => <option key={k} value={k}>{keyLabel(k)}</option>)}
          </select></label>
        <div className="flex gap-2 col-span-2">
          <button onClick={() => setF({ from: monthStart(), to: ymd(new Date()), wood: '', bucket: '' })} className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Reset</button>
          <button onClick={load} title="Refresh" className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} /></button>
          <button onClick={exportExcel} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
          <button onClick={print} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"><Printer className="w-4 h-4" /> Print</button>
        </div>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Opening Stock" value={t.opening} sub={f.from ? `before ${fmtDate(f.from)}` : 'from the start'} />
          <Kpi label="Inward" value={t.inward} sub="received in period" tone="text-green-700" />
          <Kpi label="Outward (Issue)" value={t.outward} sub="issued on approved CFT sheets" tone="text-orange-700" />
          <Kpi label="Return" value={t.return} sub="returned on approved sheets" tone="text-emerald-700" />
          <div className="rounded-xl p-4 bg-slate-800 text-white col-span-2 md:col-span-1">
            <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Closing Stock</div>
            <div className={`text-2xl font-bold mt-1 tabular-nums ${t.closing < 0 ? 'text-red-300' : ''}`}>{fmt4(t.closing)}</div>
            <div className="text-[11px] text-slate-300">CFT on {f.to ? fmtDate(f.to) : 'today'}</div>
          </div>
        </div>
      )}
      {anyTransfer ? <p className="text-xs text-purple-700">Transfers in this period: {fmt4(t.transfer_in)} CFT moved between thickness categories (wood totals unchanged).</p> : null}

      {loading && !data ? <div className="p-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        : !data?.woods?.length ? <div className="bg-white rounded-xl border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400">No wood stock yet — add wood types with their thicknesses, then enter Opening Balances or Wood Inward.</div>
        : (
          <div className={`grid gap-4 ${data.woods.length > 1 ? '2xl:grid-cols-2' : ''}`}>
            {data.woods.map((w, i) => {
              const hasT = w.totals.transfer_in || w.totals.transfer_out;
              return (
                <div key={w.wood_name} className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
                  <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-3">
                    <div>
                      <div className="font-bold text-gray-800 flex items-center gap-2"><TreePine className="w-4 h-4 text-green-700" /> {w.wood_name}
                        {!w.is_active && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500">Inactive</span>}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{w.buckets.length} thickness categor{w.buckets.length === 1 ? 'y' : 'ies'}{!w.in_master ? ' · typed on older CFT sheets' : ''}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold ${TAGS[i % TAGS.length]}`}>WOOD {String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm tabular-nums">
                      <thead><tr className="bg-gray-50 text-[10px] uppercase text-gray-500">
                        <th className="px-3 py-2 text-left">Thickness</th><th className="px-2 py-2 text-right">Opening</th><th className="px-2 py-2 text-right">Inward</th>
                        <th className="px-2 py-2 text-right">Outward</th><th className="px-2 py-2 text-right">Return</th>
                        {hasT ? <th className="px-2 py-2 text-right">Transfer</th> : null}<th className="px-3 py-2 text-right">Closing</th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {w.buckets.map(b => (
                          <tr key={b.key} onClick={() => setLedger({ wood: w.wood_name, bucket: b.key })} className="hover:bg-blue-50/50 cursor-pointer" title="Open the ledger">
                            <td className="px-3 py-2 font-semibold text-gray-800 whitespace-nowrap">{b.label}{!b.in_master ? <span className="ml-1 text-[10px] font-normal text-amber-700">(not in list)</span> : !b.is_active ? <span className="ml-1 text-[10px] font-normal text-gray-400">(inactive)</span> : null}</td>
                            <td className="px-2 py-2 text-right text-gray-600">{cell(b.opening)}</td>
                            <td className="px-2 py-2 text-right text-green-700">{cell(b.inward)}</td>
                            <td className="px-2 py-2 text-right text-orange-700">{cell(b.outward)}</td>
                            <td className="px-2 py-2 text-right text-emerald-700">{cell(b.return)}</td>
                            {hasT ? <td className="px-2 py-2 text-right text-purple-700">{b.transfer_in || b.transfer_out ? fmt4(b.transfer_in - b.transfer_out) : '—'}</td> : null}
                            <td className={`px-3 py-2 text-right font-bold ${b.closing < 0 ? 'text-red-600' : 'text-gray-900'}`}>{fmt4(b.closing)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr className="bg-gray-50 font-bold text-gray-800">
                        <td className="px-3 py-2">TOTAL CFT</td><td className="px-2 py-2 text-right">{fmt4(w.totals.opening)}</td><td className="px-2 py-2 text-right">{fmt4(w.totals.inward)}</td>
                        <td className="px-2 py-2 text-right">{fmt4(w.totals.outward)}</td><td className="px-2 py-2 text-right">{fmt4(w.totals.return)}</td>
                        {hasT ? <td className="px-2 py-2 text-right">{fmt4(w.totals.transfer_in - w.totals.transfer_out)}</td> : null}<td className="px-3 py-2 text-right">{fmt4(w.totals.closing)}</td>
                      </tr></tfoot>
                    </table>
                  </div>
                  <div className="mt-auto flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/60">
                    <span className="text-[11px] font-bold uppercase text-gray-400">Available stock</span>
                    <span className={`text-lg font-bold tabular-nums ${w.totals.closing < 0 ? 'text-red-600' : 'text-gray-900'}`}>{fmt4(w.totals.closing)} <span className="text-xs font-normal text-gray-400">CFT</span></span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      <p className="text-xs text-gray-400">Demand never changes stock. Issue and Return count only when a CFT sheet is approved. ALL CFT is its own category, never a total of the others. Negative closing stock is shown in red.</p>
      {ledger && <LedgerModal {...ledger} from={f.from} to={f.to} settings={settings} onClose={() => setLedger(null)} />}
    </div>
  );
}

function Kpi({ label, value, sub, tone = 'text-gray-900' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${value < 0 ? 'text-red-600' : tone}`}>{fmt4(value)}</div>
      <div className="text-[11px] text-gray-400">CFT · {sub}</div>
    </div>
  );
}

// Dated ledger of one wood + thickness with a running balance
function LedgerModal({ wood, bucket, from, to, settings, onClose }) {
  const navigate = useNavigate();
  const base = useLocation().pathname.startsWith('/worker') ? '/worker/cft' : '/admin/cft';
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get('/wood-stock/thickness-ledger', { params: { wood, bucket, from: from || undefined, to: to || undefined } })
      .then(r => setD(r.data)).catch(err => { toast.error(err.response?.data?.message || 'Failed to load'); onClose(); });
  }, [wood, bucket, from, to, onClose]);
  const title = `${wood} — ${keyLabel(bucket)}`;
  const detail = r => [r.label, r.length ? `${r.length}×${r.width}×${r.thickness} mm` : '', r.qty ? `${r.qty} pcs` : '', r.project_name, r.note].filter(Boolean).join(' · ');
  const cols = [
    { label: 'Date', value: r => fmtDate(r.date) }, { label: 'Type', value: r => KIND[r.kind]?.label || r.kind },
    { label: 'Document', value: r => r.ref_no }, { label: 'Details', value: detail },
    { label: 'In CFT', align: 'r', value: r => (r.in_cft ? fmt4(r.in_cft) : '') }, { label: 'Out CFT', align: 'r', value: r => (r.out_cft ? fmt4(r.out_cft) : '') },
    { label: 'Balance', align: 'r', value: r => fmt4(r.balance) }, { label: 'By', value: r => r.by_name || '' },
  ];
  const sub = `${from ? fmtDate(from) : 'Start'} – ${to ? fmtDate(to) : 'Today'}`;
  const exportExcel = async () => {
    try {
      await downloadExcel(`Wood_Ledger_${wood}_${keyLabel(bucket)}.xlsx`.replace(/\s+/g, '_'), [{
        name: 'Ledger', rows: [
          { Date: from || '', Type: 'Opening', Document: '', Details: '', 'In CFT': '', 'Out CFT': '', Balance: d.opening_cft, By: '' },
          ...d.rows.map(r => Object.fromEntries(cols.map(c => [c.label, c.align === 'r' && c.value(r) !== '' ? Number(c.value(r)) : c.value(r)]))),
        ],
      }]);
    } catch (err) { toast.error(err.message); }
  };
  const print = () => printTable({
    title, subtitle: sub, company: settings?.company_name,
    summary: d ? [['Opening', fmt4(d.opening_cft)], ['Inward', fmt4(d.summary.inward)], ['Outward', fmt4(d.summary.outward)], ['Return', fmt4(d.summary.return)], ['Closing', fmt4(d.summary.closing)]] : [],
    columns: cols, rows: d?.rows || [], rowClass: r => (r.negative ? 'neg' : ''),
  }) || toast.error('Allow pop-ups to print');
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b border-gray-100">
          <div><h3 className="font-bold text-gray-900">{title}</h3><p className="text-xs text-gray-500">{sub}</p></div>
          <div className="flex items-center gap-2">
            {d && <button onClick={exportExcel} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>}
            {d && <button onClick={print} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700"><Printer className="w-3.5 h-3.5" /> Print</button>}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
          </div>
        </div>
        {!d ? <div className="p-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <div className="overflow-y-auto">
            <div className="flex flex-wrap gap-2 px-5 py-3 text-xs">
              {[['Opening', d.opening_cft], ['Inward', d.summary.inward], ['Outward', d.summary.outward], ['Return', d.summary.return],
                ...(d.summary.transfer_in || d.summary.transfer_out ? [['Transfer In', d.summary.transfer_in], ['Transfer Out', d.summary.transfer_out]] : []), ['Closing', d.summary.closing]].map(([k, v]) => (
                <span key={k} className={`px-2.5 py-1 rounded-lg border ${k === 'Closing' ? 'bg-slate-800 text-white border-slate-800' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>{k} <b className="tabular-nums">{fmt4(v)}</b></span>
              ))}
              {d.rows.some(r => r.negative) && <span className="flex items-center gap-1 text-red-600 font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Stock went below zero</span>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[860px] tabular-nums">
                <thead><tr className="bg-gray-50 text-left text-[11px] uppercase text-gray-500">
                  <th className="px-3 py-2">Date</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Document</th><th className="px-3 py-2">Details</th>
                  <th className="px-3 py-2 text-right">In CFT</th><th className="px-3 py-2 text-right">Out CFT</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2">By</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="bg-gray-50/80 text-xs font-semibold text-gray-600"><td className="px-3 py-2" colSpan={6}>Opening Stock {from ? `(before ${fmtDate(from)})` : ''}</td><td className="px-3 py-2 text-right">{fmt4(d.opening_cft)}</td><td></td></tr>
                  {d.rows.map(r => (
                    <tr key={r.key} className={r.negative ? 'bg-red-50' : ''}>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${KIND[r.kind]?.cls}`}>{KIND[r.kind]?.label}</span></td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.src === 'cft'
                        ? <button onClick={() => navigate(`${base}/${r.doc_id}`)} className="text-blue-700 hover:underline">{r.ref_no}</button> : r.ref_no}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[22rem]">{detail(r) || '—'}</td>
                      <td className="px-3 py-2 text-right text-green-700">{r.in_cft ? fmt4(r.in_cft) : ''}</td>
                      <td className="px-3 py-2 text-right text-orange-700">{r.out_cft ? fmt4(r.out_cft) : ''}</td>
                      <td className={`px-3 py-2 text-right font-bold ${r.negative ? 'text-red-600' : 'text-gray-800'}`}>{fmt4(r.balance)}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap" title={r.entered_at ? new Date(r.entered_at).toLocaleString() : ''}>{r.by_name || '—'}</td>
                    </tr>
                  ))}
                  {!d.rows.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-gray-400">No movement in this period</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
